import { useEffect, useState } from 'react'
import { Drawer } from 'antd'
import { BellOutlined, LoginOutlined, MenuOutlined, SearchOutlined } from '@ant-design/icons'
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom'
import BrandMark from './BrandMark'
import SeoManager from './SeoManager'
import { previewNotice } from '../data/content'

const navItems = [
  { to: '/', label: '首页', end: true },
  { to: '/news', label: '资讯' },
  { to: '/events', label: '大事件' },
  { to: '/companies', label: '公司' },
  { to: '/markets', label: '市场' },
  { to: '/daily', label: '日报' },
  { to: '/community', label: '社区' },
]

export type SpaceUser = {
  id: number
  displayName: string
  login: string
  avatarUrl?: string | null
  role?: string
}

export type SpaceOutletContext = {
  user: SpaceUser | null
  authReady: boolean
  login: (returnPath?: string) => void
  logout: () => Promise<void>
}

export default function AppShell() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [user, setUser] = useState<SpaceUser | null>(null)
  const [authReady, setAuthReady] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()

  useEffect(() => {
    const controller = new AbortController()
    let active = true
    fetch('/api/space/auth/me', { credentials: 'include', signal: controller.signal })
      .then(response => response.ok ? response.json() as Promise<{ user: SpaceUser | null }> : { user: null })
      .then(data => { if (active) setUser(data.user) })
      .catch(error => {
        if (active && error instanceof Error && error.name !== 'AbortError') setUser(null)
      })
      .finally(() => { if (active) setAuthReady(true) })
    return () => {
      active = false
      controller.abort()
    }
  }, [])

  const login = (requestedReturnPath?: string) => {
    const origin = import.meta.env.VITE_AUTH_ORIGIN || 'https://auth.since.team'
    const returnPath = requestedReturnPath || `${location.pathname}${location.search}`
    window.location.href = `${origin}/start?target=space&return_path=${encodeURIComponent(returnPath)}`
  }

  const logout = async () => {
    await fetch('/api/space/auth/logout', { method: 'POST', credentials: 'include' })
    setUser(null)
  }

  const runSearch = () => {
    const query = search.trim()
    navigate(query ? `/news?q=${encodeURIComponent(query)}` : '/news')
    setSearchOpen(false)
    setDrawerOpen(false)
  }

  return (
    <div className="app-shell">
      <SeoManager />
      <header className="site-header">
        <button className="brand" type="button" onClick={() => navigate('/')} aria-label="返回 Space Since 首页">
          <BrandMark />
          <span>Space Since</span>
        </button>
        <nav className="desktop-nav" aria-label="主导航">
          {navItems.map(item => <NavLink key={item.to} to={item.to} end={item.end}>{item.label}</NavLink>)}
        </nav>
        <div className="header-tools">
          <button type="button" className="header-tool" onClick={() => setSearchOpen(value => !value)}><SearchOutlined /><span>搜索</span></button>
          <button type="button" className="header-tool" onClick={() => navigate('/daily#subscribe')}><BellOutlined /><span>订阅</span></button>
          <button type="button" className="header-tool" onClick={() => { void (user ? logout() : login()) }} title={user ? '退出登录' : '登录统一账号'}><LoginOutlined /><span>{user?.displayName || user?.login || '登录'}</span></button>
          <button type="button" className="mobile-menu-button" onClick={() => setDrawerOpen(true)} aria-label="打开导航"><MenuOutlined /></button>
        </div>
        {searchOpen ? (
          <form className="header-search" onSubmit={event => { event.preventDefault(); runSearch() }}>
            <SearchOutlined />
            <input autoFocus value={search} onChange={event => setSearch(event.target.value)} placeholder="搜索公司、事件或资讯" aria-label="搜索" />
            <button type="submit">搜索</button>
          </form>
        ) : null}
      </header>
      <div className="preview-banner" role="note">{previewNotice}</div>
      <main className="site-main">
        <Outlet context={{ user, authReady, login, logout } satisfies SpaceOutletContext} />
      </main>
      <footer className="site-footer">
        <div className="site-footer__brand"><BrandMark /><div><strong>Space Since</strong><span>商业航天情报站</span></div></div>
        <p>用来源和证据连接资讯、事件、公司与市场。本站信息不构成投资建议。</p>
        <div className="site-footer__links"><a href="/daily">日报</a><a href="/community">社区规范</a><a href="mailto:space@since.team">联系我们</a></div>
      </footer>
      <Drawer className="mobile-drawer" open={drawerOpen} onClose={() => setDrawerOpen(false)} placement="right" width="min(86vw, 340px)" title={<span className="drawer-brand"><BrandMark /> Space Since</span>}>
        <nav className="mobile-nav" aria-label="移动端导航">
          {navItems.map(item => <NavLink key={item.to} to={item.to} end={item.end} onClick={() => setDrawerOpen(false)}>{item.label}</NavLink>)}
        </nav>
        <div className="mobile-drawer__actions">
          <button type="button" onClick={() => { setSearchOpen(true); setDrawerOpen(false) }}><SearchOutlined /> 搜索</button>
          <button type="button" onClick={() => { void (user ? logout() : login()) }}><LoginOutlined /> {user ? `${user.displayName || user.login} · 退出` : '登录统一账号'}</button>
        </div>
      </Drawer>
    </div>
  )
}
